import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import MobileBottomNav from '../../components/feature/MobileBottomNav';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { categories } from '../../mocks/categories';
import { featuredProducts } from '../../mocks/products';
import { testimonials } from '../../mocks/testimonials';

/* ───────── Ticker ───────── */
function Ticker() {
  const items = [
    'ÜCRETSİZ KARGO — 500₺ ÜZERİ SİPARİŞLERDE',
    'KİŞİYE ÖZEL TASARIM',
    'GÜVENLI ÖDEME',
    '24 SAATTE TEKLİF',
    'EL YAPIMI ÜRÜNLER',
  ];
  const repeated = [...items, ...items];
  return (
    <div className="bg-gray-900 text-white overflow-hidden py-2.5">
      <div className="flex animate-[ticker_30s_linear_infinite] whitespace-nowrap w-max">
        {repeated.map((item, i) => (
          <span key={i} className="inline-flex items-center gap-3 text-xs font-semibold tracking-widest px-10">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block"></span>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ───────── Product Card ───────── */
function ProductCard({ product }: { product: typeof featuredProducts[0] }) {
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);
  const [wished, setWished] = useState(false);

  const handleCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const ex = cart.find((i: { id: number }) => i.id === product.id);
    if (ex) ex.quantity += 1;
    else cart.push({ ...product, quantity: 1 });
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div
      onClick={() => navigate(`/urun/${product.slug}`)}
      className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-gray-200 transition-all duration-300 cursor-pointer flex flex-col"
    >
      {/* Image */}
      <div className="relative bg-[#f7f5f2] overflow-hidden" style={{ paddingBottom: '110%' }}>
        <img
          src={product.image}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500"
        />
        {product.discount > 0 && (
          <span className="absolute top-3 left-3 bg-red-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-md z-10">
            %{product.discount} İNDİRİM
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); setWished(w => !w); }}
          className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full z-10 transition-all cursor-pointer ${wished ? 'bg-red-50 text-red-500' : 'bg-white/90 text-gray-400 hover:text-red-400'}`}
        >
          <i className={wished ? 'ri-heart-fill' : 'ri-heart-line'}></i>
        </button>
        {product.stock > 0 && product.stock <= 5 && (
          <span className="absolute bottom-3 left-3 bg-yellow-400 text-gray-900 text-[11px] font-bold px-2.5 py-1 rounded-md z-10">
            Son {product.stock} adet!
          </span>
        )}
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-1">{product.category}</p>
        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2 leading-snug flex-1">{product.name}</h3>
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-lg font-bold text-gray-900">{product.price.toLocaleString('tr-TR')} ₺</span>
          {product.oldPrice > 0 && (
            <span className="text-sm text-gray-400 line-through">{product.oldPrice.toLocaleString('tr-TR')} ₺</span>
          )}
        </div>
        {product.reviewCount > 0 && (
          <div className="flex items-center gap-1 mb-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <i key={i} className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-yellow-400 text-xs`}></i>
            ))}
            <span className="text-xs text-gray-400 ml-0.5">({product.reviewCount})</span>
          </div>
        )}
        <button
          onClick={handleCart}
          className={`w-full py-2.5 text-sm font-bold rounded-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer ${added ? 'bg-green-500 text-white' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
        >
          <i className={added ? 'ri-check-line' : 'ri-shopping-cart-line'}></i>
          {added ? 'Eklendi!' : 'Sepete Ekle'}
        </button>
      </div>
    </div>
  );
}

/* ───────── Counter ───────── */
function Counter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      let start = 0;
      const step = Math.ceil(target / 60);
      const timer = setInterval(() => {
        start += step;
        if (start >= target) { setCount(target); clearInterval(timer); }
        else setCount(start);
      }, 25);
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{count.toLocaleString('tr-TR')}{suffix}</span>;
}

/* ───────── HOME PAGE ───────── */
export default function Home() {
  const navigate = useNavigate();
  const [testimonialIdx, setTestimonialIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTestimonialIdx(i => (i + 1) % testimonials.length), 5000);
    return () => clearInterval(t);
  }, []);

  const displayProducts = featuredProducts.slice(0, 8);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <style>{`
        @keyframes ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.93); } to { opacity: 1; transform: scale(1); } }
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Playfair+Display:wght@700;900&display=swap');
      `}</style>

      <Navbar />
      <WhatsAppButton />

      {/* ─── HERO: DESKTOP ─── */}
      <section className="hidden md:flex relative min-h-screen flex-col justify-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20flat%20lay%20of%20beautifully%20handcrafted%20decorative%20wall%20art%20frames%20arranged%20on%20a%20warm%20cream%20textured%20surface%20with%20soft%20pink%20roses%20dried%20flowers%20and%20golden%20accents%20scattered%20around%20natural%20studio%20lighting%20professional%20product%20photography%20warm%20tones%20minimal%20background&width=1920&height=1080&seq=hero_main_2026&orientation=landscape"
            alt="Hediyatik hero"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10"></div>
        </div>

        <div className="relative z-10 w-full pb-16 pt-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <p className="text-orange-400 text-xs font-bold tracking-[0.3em] uppercase mb-4">El Yapımı • Kişiye Özel</p>
              <h1 className="text-7xl lg:text-8xl font-black font-['Playfair_Display'] text-white leading-[1.05] mb-6">
                ÖZEL<br />
                <span className="text-orange-400">GÜNLER</span><br />
                İÇİN
              </h1>
              <p className="text-lg text-gray-200 mb-8 max-w-lg leading-relaxed">
                Hayatınızın en kıymetli anlarını unutulmaz el yapımı hediyelere dönüştürüyoruz. Her tasarım sizin hikayeniz.
              </p>
              <div className="flex flex-row gap-3">
                <button
                  onClick={() => navigate('/kategoriler')}
                  className="px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition-all whitespace-nowrap text-sm tracking-wide cursor-pointer active:bg-orange-600 transition-colors"
                >
                  <i className="ri-grid-fill text-base"></i>
                  ÜRÜNLERE GÖZA AT
                </button>
                <button
                  onClick={() => navigate('/ozel-tasarim')}
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/40 text-white font-bold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap text-sm tracking-wide cursor-pointer active:bg-white/20 transition-colors"
                >
                  <i className="ri-edit-2-line text-orange-400 text-base"></i>
                  ÖZEL TASARIM İSTE
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="relative z-10 bg-white/10 backdrop-blur-md border-t border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Mutlu Müşteri', target: 2400, suffix: '+' },
                { label: 'Tasarım Seçeneği', target: 150, suffix: '+' },
                { label: 'Ülkeden Sipariş', target: 12, suffix: '' },
                { label: 'Yıldız Değerlendirme', target: 5, suffix: '/5' },
              ].map((s, i) => (
                <div key={i} className="text-center text-white">
                  <p className="text-2xl font-black"><Counter target={s.target} suffix={s.suffix} /></p>
                  <p className="text-xs text-white/70 mt-0.5 font-medium">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── HERO: MOBİL ─── */}
      <section className="md:hidden relative overflow-hidden bg-[#1a1a18]">
        {/* Arka plan görseli */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=close%20up%20handcrafted%20stone%20powder%20marble%20effect%20decorative%20frame%20with%20delicate%20pink%20flowers%20soft%20cream%20background%20warm%20natural%20lighting%20artisan%20craft%20product%20photography&width=750&height=900&seq=hero_mobile_2026&orientation=portrait"
            alt="Hediyatik"
            className="w-full h-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a18]/60 via-[#1a1a18]/30 to-[#1a1a18]/95"></div>
        </div>

        <div className="relative z-10 pt-20 pb-6 px-5 flex flex-col" style={{ minHeight: '100svh' }}>

          {/* Üst badge */}
          <div className="flex items-center gap-2 mb-5" style={{ animation: 'fadeSlideUp 0.5s ease both 0.1s' }}>
            <span className="inline-flex items-center gap-1.5 bg-orange-500/20 border border-orange-500/30 text-orange-400 text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block"></span>
              El Yapımı · Kişiye Özel
            </span>
          </div>

          {/* Ana başlık */}
          <div className="mb-6 flex-1" style={{ animation: 'fadeSlideUp 0.5s ease both 0.2s' }}>
            <h1 className="text-5xl font-black font-['Playfair_Display'] text-white leading-[1.05] mb-3">
              ÖZEL<br />
              <span className="text-orange-400">GÜNLER</span><br />
              İÇİN
            </h1>
            <p className="text-sm text-gray-300 leading-relaxed max-w-xs">
              El yapımı, kişiselleştirilebilir tasarımlar ile sevdiklerinize unutulmaz hediyeler.
            </p>
          </div>

          {/* Ürün önizleme kaydırma şeridi */}
          <div className="mb-5 -mx-5 px-5 overflow-x-auto flex gap-3 pb-2 scrollbar-hide" style={{ animation: 'fadeSlideUp 0.5s ease both 0.3s' }}>
            {featuredProducts.slice(0, 5).map((p, i) => (
              <div
                key={p.id}
                onClick={() => navigate(`/urun/${p.slug}`)}
                className="flex-shrink-0 cursor-pointer"
                style={{ animation: `scaleIn 0.4s ease both ${0.35 + i * 0.07}s` }}
              >
                <div className="w-20 h-20 rounded-2xl overflow-hidden bg-white/10 border border-white/20">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-contain p-1.5"
                  />
                </div>
                <p className="text-white text-[9px] font-semibold text-center mt-1.5 w-20 line-clamp-1 opacity-70">{p.name.split(' ')[0]}</p>
              </div>
            ))}
            <div
              onClick={() => navigate('/kategoriler')}
              className="flex-shrink-0 cursor-pointer"
            >
              <div className="w-20 h-20 rounded-2xl bg-orange-500/20 border border-orange-500/40 flex flex-col items-center justify-center gap-1">
                <i className="ri-arrow-right-line text-orange-400 text-xl"></i>
                <span className="text-orange-400 text-[9px] font-bold">Tümü</span>
              </div>
            </div>
          </div>

          {/* CTA butonları */}
          <div className="flex flex-col gap-2.5 mb-5" style={{ animation: 'fadeSlideUp 0.5s ease both 0.5s' }}>
            <button
              onClick={() => navigate('/kategoriler')}
              className="w-full py-4 bg-orange-500 text-white font-bold rounded-2xl text-sm tracking-wide flex items-center justify-center gap-2 cursor-pointer active:bg-orange-600 transition-colors"
            >
              <i className="ri-grid-fill text-base"></i>
              ÜRÜNLERE GÖZA AT
            </button>
            <button
              onClick={() => navigate('/ozel-tasarim')}
              className="w-full py-3.5 bg-white/10 border border-white/25 text-white font-bold rounded-2xl text-sm tracking-wide flex items-center justify-center gap-2 cursor-pointer active:bg-white/20 transition-colors"
            >
              <i className="ri-edit-2-line text-orange-400 text-base"></i>
              ÖZEL TASARIM İSTE
            </button>
          </div>

          {/* Mobil mini stats */}
          <div className="grid grid-cols-3 gap-2" style={{ animation: 'fadeSlideUp 0.5s ease both 0.6s' }}>
            {[
              { val: '2.4K+', label: 'Müşteri', icon: 'ri-heart-fill' },
              { val: '4.9★', label: 'Puan', icon: 'ri-star-fill' },
              { val: '24sa', label: 'Teklif', icon: 'ri-time-fill' },
            ].map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-5 hover:bg-white/10 transition-all">
                <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center mb-2 md:mb-3">
                  <i className={`${s.icon} text-xl md:text-2xl text-orange-400`}></i>
                </div>
                <h4 className="text-white font-bold text-sm mb-1">{s.label}</h4>
                <p className="text-gray-500 text-xs">{s.val}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TICKER ─── */}
      <Ticker />

      {/* ─── BEST SELLERS ─── */}
      <section className="py-10 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-6 md:mb-10 md:block md:text-center">
            <div>
              <p className="text-orange-500 text-xs font-bold tracking-[0.3em] uppercase mb-1 md:mb-2">Çok Satanlar</p>
              <h2 className="text-2xl md:text-5xl font-black font-['Playfair_Display'] text-gray-900 leading-tight">
                EN ÇOK<br className="md:hidden" /> TERCİH EDİLENLER
              </h2>
            </div>
            <button
              onClick={() => navigate('/kategoriler')}
              className="md:hidden shrink-0 ml-4 text-xs font-bold text-orange-500 flex items-center gap-1 whitespace-nowrap cursor-pointer"
            >
              Tümü <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>

          {/* Mobilde yatay kaydırma, desktop grid */}
          <div className="md:hidden -mx-4 px-4 overflow-x-auto flex gap-3 pb-2 scrollbar-hide">
            {displayProducts.map(p => (
              <div key={p.id} className="flex-shrink-0 w-44">
                <ProductCard product={p} />
              </div>
            ))}
          </div>
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-product-shop>
            {displayProducts.map(p => <ProductCard key={p.id} product={p} />)}
          </div>

          <div className="text-center mt-8 hidden md:block">
            <button
              onClick={() => navigate('/kategoriler')}
              className="px-10 py-3.5 border-2 border-gray-900 text-gray-900 font-bold rounded-lg hover:bg-gray-900 hover:text-white transition-all text-sm tracking-wide whitespace-nowrap cursor-pointer"
            >
              TÜM ÜRÜNLERİ GÖR
            </button>
          </div>
        </div>
      </section>

      {/* ─── CATEGORIES ─── */}
      <section className="py-10 md:py-16 bg-[#faf9f7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 md:mb-10">
            <p className="text-orange-500 text-xs font-bold tracking-[0.3em] uppercase mb-1 md:mb-2">Kategoriler</p>
            <h2 className="text-2xl md:text-5xl font-black font-['Playfair_Display'] text-gray-900">
              ÖZEL GÜNLER
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {categories.map((cat) => (
              <div
                key={cat.id}
                onClick={() => navigate('/kategoriler')}
                className="group relative overflow-hidden rounded-2xl cursor-pointer bg-white border border-gray-100 hover:border-gray-200 transition-all"
              >
                {cat.badge && (
                  <span className="absolute top-2.5 left-2.5 z-10 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide">
                    {cat.badge}
                  </span>
                )}
                <div className="w-full overflow-hidden" style={{ paddingBottom: '100%', position: 'relative' }}>
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                    <h3 className="text-white font-bold text-xs md:text-sm leading-tight">{cat.name}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CUSTOM DESIGN CTA ─── */}
      <section className="py-10 md:py-16 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <p className="text-orange-400 text-xs font-bold tracking-[0.3em] uppercase mb-3 md:mb-4">Sadece Size Özel</p>
              <h2 className="text-3xl md:text-5xl font-black font-['Playfair_Display'] text-white leading-tight mb-4 md:mb-6">
                ÖZEL TASARIM<br />
                <span className="text-orange-400">HAYALİNİZİ</span><br />
                YAPALIM
              </h2>
              <p className="text-gray-400 mb-6 md:mb-8 leading-relaxed text-sm">
                Konseptinizi seçin, figürlerinizi belirleyin, renkleri özelleştirin. 24 saat içinde size özel tasarım teklifiniz WhatsApp üzerinden gelsin.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/ozel-tasarim')}
                  className="px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition-all whitespace-nowrap text-sm tracking-wide cursor-pointer"
                >
                  ÖZEL TASARIM İSTE
                </button>
                <a
                  href="https://wa.me/905551234567"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 border border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition-all whitespace-nowrap text-sm tracking-wide flex items-center justify-center gap-2"
                >
                  <i className="ri-whatsapp-line text-green-400 text-lg"></i>
                  WhatsApp'tan Yaz
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: 'ri-palette-line', title: 'Renk Seçimi', desc: 'İstediğiniz renk kombinasyonu' },
                { icon: 'ri-user-smile-line', title: 'Kişiselleştirme', desc: 'İsim, tarih ve özel mesaj' },
                { icon: 'ri-time-line', title: '24 Saat', desc: 'Hızlı tasarım teklifi' },
                { icon: 'ri-gift-2-line', title: 'Hediye Paketi', desc: 'Lüks hediye kutusunda teslim' },
              ].map((f, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-5 hover:bg-white/10 transition-all">
                  <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center mb-2 md:mb-3">
                    <i className={`${f.icon} text-xl md:text-2xl text-orange-400`}></i>
                  </div>
                  <h4 className="text-white font-bold text-sm mb-1">{f.title}</h4>
                  <p className="text-gray-500 text-xs">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHY US ─── */}
      <section className="py-10 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <p className="text-orange-500 text-xs font-bold tracking-[0.3em] uppercase mb-1 md:mb-2">Neden Biz?</p>
            <h2 className="text-2xl md:text-5xl font-black font-['Playfair_Display'] text-gray-900">
              FARKI HİSSEDİN
            </h2>
          </div>
          {/* Mobilde yatay kaydırma */}
          <div className="md:hidden -mx-4 px-4 overflow-x-auto flex gap-3 pb-2 scrollbar-hide">
            {[
              { icon: 'ri-hand-heart-line', title: '%100 El Yapımı', desc: 'Her ürün özenle el işçiliğiyle' },
              { icon: 'ri-shield-check-line', title: 'Güvenli Ödeme', desc: 'SSL sertifikası' },
              { icon: 'ri-truck-line', title: 'Hızlı Kargo', desc: '3-7 iş günü teslim' },
              { icon: 'ri-award-line', title: 'Kalite Garantisi', desc: 'Solmaz renkler' },
              { icon: 'ri-customer-service-2-line', title: '7/24 Destek', desc: 'WhatsApp destek' },
            ].map((f, i) => (
              <div key={i} className="flex-shrink-0 w-32 text-center p-4 rounded-2xl bg-[#faf9f7]">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2 bg-white rounded-xl">
                  <i className={`${f.icon} text-xl text-orange-500`}></i>
                </div>
                <h4 className="font-bold text-gray-900 text-xs mb-0.5 leading-snug">{f.title}</h4>
                <p className="text-gray-400 text-[10px] leading-tight">{f.desc}</p>
              </div>
            ))}
          </div>
          <div className="hidden md:grid md:grid-cols-5 gap-4">
            {[
              { icon: 'ri-hand-heart-line', title: '% 100 El Yapımı', desc: 'Her ürün özenle el işçiliğiyle üretilir' },
              { icon: 'ri-shield-check-line', title: 'Güvenli Ödeme', desc: 'SSL sertifikası ile güvenli alışveriş' },
              { icon: 'ri-truck-line', title: 'Hızlı Kargo', desc: '3-7 iş günü kapı teslimi' },
              { icon: 'ri-award-line', title: 'Kalite Garantisi', desc: 'UV baskı ile solmaz renkler' },
              { icon: 'ri-customer-service-2-line', title: '7/24 Destek', desc: 'WhatsApp ile her an ulaşın' },
            ].map((f, i) => (
              <div key={i} className="text-center p-6 rounded-xl bg-[#faf9f7] hover:bg-orange-50 transition-all group">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-white rounded-xl group-hover:bg-orange-100 transition-all">
                  <i className={`${f.icon} text-2xl text-orange-500`}></i>
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">{f.title}</h4>
                <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="py-10 md:py-16 bg-[#faf9f7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 md:mb-10">
            <p className="text-orange-500 text-xs font-bold tracking-[0.3em] uppercase mb-1 md:mb-2">Müşteri Yorumları</p>
            <h2 className="text-2xl md:text-5xl font-black font-['Playfair_Display'] text-gray-900">
              ONLAR NE DEDİ?
            </h2>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 md:gap-4 max-w-lg mx-auto mb-6 md:mb-10">
            {[
              { val: '2.4K+', label: 'Mutlu Müşteri' },
              { val: '4.9', label: 'Ortalama Puan' },
              { val: '50K+', label: 'Instagram Takipçi' },
            ].map((s, i) => (
              <div key={i} className="text-center bg-white rounded-xl p-3 md:p-4">
                <p className="text-xl md:text-2xl font-black text-gray-900">{s.val}</p>
                <p className="text-[10px] md:text-xs text-gray-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Mobilde tek kolon, desktopta 3 grid */}
          <div className="md:hidden space-y-3">
            {testimonials.slice(0, 3).map((t, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl p-5 border-2 transition-all ${i === testimonialIdx % 3 ? 'border-orange-400' : 'border-transparent'}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, si) => (
                      <i key={si} className="ri-star-fill text-yellow-400 text-xs"></i>
                    ))}
                  </div>
                  {t.verified && (
                    <span className="text-[10px] text-green-600 flex items-center gap-1 font-semibold">
                      <i className="ri-checkbox-circle-fill"></i> Doğrulandı
                    </span>
                  )}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-3 line-clamp-3">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                    <img src={t.image} alt={t.author} className="w-full h-full object-cover" />
                  </div>
                  <p className="font-bold text-gray-900 text-sm">{t.author}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden md:grid md:grid-cols-3 gap-5">
            {testimonials.slice(0, 3).map((t, i) => (
              <div
                key={i}
                className={`bg-white rounded-xl p-6 border-2 transition-all ${i === testimonialIdx % 3 ? 'border-orange-400' : 'border-transparent'}`}
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, si) => (
                    <i key={si} className="ri-star-fill text-yellow-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4 line-clamp-4">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                    <img src={t.image} alt={t.author} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{t.author}</p>
                    {t.verified && (
                      <p className="text-[10px] text-green-600 flex items-center gap-1">
                        <i className="ri-checkbox-circle-fill"></i> Doğrulandı
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-5 md:mt-6">
            {testimonials.slice(0, 3).map((_, i) => (
              <button
                key={i}
                onClick={() => setTestimonialIdx(i)}
                className={`h-2 rounded-full transition-all cursor-pointer ${i === testimonialIdx % 3 ? 'w-8 bg-orange-500' : 'w-2 bg-gray-300'}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ─── INSTAGRAM / SOCIAL ─── */}
      <section className="py-10 md:py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-orange-400 text-xs font-bold tracking-[0.3em] uppercase mb-2">Instagram</p>
          <h2 className="text-2xl md:text-3xl font-black font-['Playfair_Display'] mb-2 md:mb-3">TARZINI PAYLAŞ</h2>
          <p className="text-gray-400 text-sm mb-6 md:mb-8">Ürünlerinizi etiketleyin, paylaşımlarınız öne çıksın</p>
          <a
            href="https://instagram.com"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold rounded-lg hover:opacity-90 transition-all text-sm whitespace-nowrap"
          >
            <i className="ri-instagram-line text-lg"></i>
            @hediyatik Hesabını Takip Et
          </a>
        </div>
      </section>

      <Footer />
      <MobileBottomNav />
    </div>
  );
}
